import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FlaskConicalIcon, TrendingUpIcon, RocketIcon } from 'lucide-react';
const pillars = [{
  icon: FlaskConicalIcon,
  title: 'The Vault',
  description: 'We cast a wide net for ideas — good, bad, tiny, wild. Everything goes into the vault first. A safe space where diverse students question assumptions, propose unconventional strategies, and map unexplored markets. From this vault, the most promising ideas enter our validation pipeline.'
}, {
  icon: TrendingUpIcon,
  title: 'Validation Pipeline',
  description: 'Ideas from the vault undergo continuous hypothesis testing via data modeling, feature engineering, and simulation. We run validation sprints with pre-registered success criteria to separate signal from noise.'
}, {
  icon: RocketIcon,
  title: 'Execution',
  description: 'The most compelling validated ideas advance to prototype. We deploy models, build data products, and ship real implementations — all documented with rigorous post-mortems to compound our learning.'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 50,
    scale: 0.9
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export function WhatWeDo() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section ref={ref} className="w-full py-32 px-6 bg-navy-900" data-id="element-374">
      <div className="max-w-6xl mx-auto" data-id="element-375">
        <motion.h2 initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-4xl md:text-5xl font-bold mb-16 text-center text-white" data-id="element-376">
          What We Do
        </motion.h2>
        <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="grid md:grid-cols-3 gap-8" data-id="element-377">
          {pillars.map((pillar, index) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.title} variants={itemVariants} whileHover={{
            y: -10,
            scale: 1.02,
            transition: {
              duration: 0.3
            }
          }} className="group relative bg-navy-800 border-2 border-navy-700 rounded-lg p-8 hover:border-steel transition-all duration-300 shadow-lg" data-id="element-378">
                <motion.div initial={{
              scale: 0,
              rotate: -180
            }} animate={inView ? {
              scale: 1,
              rotate: 0
            } : {}} transition={{
              duration: 0.6,
              delay: 0.5 + index * 0.15
            }} className="mb-6 w-16 h-16 bg-navy-700 rounded-lg flex items-center justify-center group-hover:bg-navy-600 transition-colors duration-300" data-id="element-379">
                  <Icon className="w-8 h-8 text-white" data-id="element-380" />
                </motion.div>
                <h3 className="text-2xl font-bold mb-4 text-white" data-id="element-381">
                  {pillar.title}
                </h3>
                <p className="text-steel leading-relaxed" data-id="element-382">
                  {pillar.description}
                </p>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}